import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleUserRound, Plane } from "lucide-react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { CountryCode } from "libphonenumber-js";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { GooglePlaceAutocomplete } from "@/components/ui/customs/autocomplete";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { ControlledCountryComboboxCountryCode } from "@/components/ui/customs/controlled-combobox-countryCode";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import StyledPhoneInput from "@/components/styled/StyledPhoneInput";
import { Separator } from "@/components/ui/separator";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { GetAllCompanyDto, GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";
import { PELICAN_AIR_SERVICES_SHIPPER_INFO } from "@/constants/pelicanAirServices";
import { FedexAddressValidationActions } from "../../fedexAddressValidation/fedexAddressValidationActions";
import { parseGooglePlaceAddress } from "@/lib/google-address.util";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";

export const ShipperInfo = ({
  expedition,
  company,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const translateMessage = useTranslateMessage();
  const SHIPPER_TYPE_OPTIONS_BO = useMemo(
    () => [
      { label: "Pelican Air Services", value: "PELICAN_AIR_SERVICES" },
      {
        label: t("shipperInfo.shipperTypes.clientPickupLocation"),
        value: "CLIENT_PICKUP_LOCATION",
      },
      { label: t("shipperInfo.shipperTypes.other"), value: "OTHER" },
    ],
    [t],
  );
  const methods = useFormContext<QuotationUpdateBoFormData>();

  const countryCode = useWatch({
    control: methods.control,
    name: "shipperInfo.countryCode",
  });
  const showStateOrProvince = countryCode === "CA" || countryCode === "US";
  const showPostalCode = countryRequiresPostalCode(countryCode);

  const handlePlaceSelect = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      const parsed = parseGooglePlaceAddress(place, countryCode);
      if (!parsed) return;

      methods.setValue(`shipperInfo.streetLines.0`, parsed.street);
      methods.setValue(`shipperInfo.city`, parsed.city);
      methods.setValue(`shipperInfo.postalCode`, parsed.postalCode);
      if (showStateOrProvince) {
        methods.setValue(`shipperInfo.stateOrProvinceCode`, parsed.stateOrProvinceCode);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods],
  );

  const pickupLocation = company.pickUpLocations.find(
    (loc) => loc.id === expedition.pickupInfo?.pickupLocation?.id,
  );
  const default_client_pickup_location = {
    companyName: pickupLocation?.name || "",
    contactName: "",
    phone: pickupLocation?.contactNumber || "",
    email: expedition.createdBy?.email || "",
    streetLines: [pickupLocation?.street || "", ""],
    city: pickupLocation?.city || "",
    countryCode: "FR",
    stateOrProvinceCode: "",
    postalCode: pickupLocation?.postalCode || "",
    residential: false,
    taxId: "",
  };

  const default_other = {
    countryCode: "FR",
    stateOrProvinceCode: "",
    postalCode: "",
    residential: false,
    taxId: "",
    contactName: "",
    companyName: "",
    phone: "",
    email: "",
    streetLines: ["", ""],
    city: "",
  };

  const getDefaultShipperType = () => {
    if (
      expedition.shipperInfo?.companyName === PELICAN_AIR_SERVICES_SHIPPER_INFO.companyName ||
      !expedition.shipperInfo
    )
      return "PELICAN_AIR_SERVICES";
    if (expedition.shipperInfo?.companyName === pickupLocation?.name)
      return "CLIENT_PICKUP_LOCATION";
    return "OTHER";
  };

  const [shipperType, setShipperType] = useState<
    "PELICAN_AIR_SERVICES" | "CLIENT_PICKUP_LOCATION" | "OTHER"
  >(getDefaultShipperType());

  const handleShipperTypeChange = (value: string) => {
    setShipperType(value as "PELICAN_AIR_SERVICES" | "CLIENT_PICKUP_LOCATION" | "OTHER");
    if (value === "PELICAN_AIR_SERVICES") {
      methods.setValue(
        "shipperInfo",
        { ...PELICAN_AIR_SERVICES_SHIPPER_INFO },
        { shouldDirty: true, shouldValidate: false },
      );
    } else if (value === "CLIENT_PICKUP_LOCATION") {
      methods.setValue("shipperInfo", default_client_pickup_location, {
        shouldDirty: true,
        shouldValidate: false,
      });
    } else {
      methods.setValue("shipperInfo", default_other, { shouldDirty: true, shouldValidate: false });
    }
  };

  const shipperTypeLabel =
    SHIPPER_TYPE_OPTIONS_BO.find((o) => o.value === shipperType)?.label ?? "";

  return (
    <Accordion type="single" collapsible className="flex w-full @3xl:col-span-2">
      <AccordionItem
        value="shipperInfo"
        className="flex w-full flex-col rounded-lg border border-slate-300 bg-white px-2 shadow-sm @xl:px-3"
      >
        <AccordionTrigger className="items-center gap-3 py-3 !no-underline hover:no-underline">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <div className="flex flex-row items-center gap-2">
              <CircleUserRound className="h-6 w-6 shrink-0 !rotate-0 text-blue-600" />
              <span className="text-lg font-bold">{t("shipperInfo.title")}</span>
            </div>
            {shipperType === "PELICAN_AIR_SERVICES" ? (
              <img
                src="/images/pelican-logo.png"
                alt="Pelican Air Services"
                className="h-6 w-6 object-contain"
              />
            ) : (
              <Plane className="h-6 w-6 shrink-0 !rotate-0 text-blue-600" />
            )}
            <span className="truncate text-sm font-medium text-slate-600 uppercase">
              {shipperTypeLabel}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent forceMount className="flex w-full flex-col gap-2">
          <SelectV2
            options={SHIPPER_TYPE_OPTIONS_BO}
            label={t("shipperInfo.shipperTypeLabel")}
            wrapperClassName="w-full"
            onChange={(value) => handleShipperTypeChange(value)}
            value={shipperType}
          />
          <Separator className="my-2" />
          <div className="flex flex-col gap-2 @xl:flex-row">
            <ControlledUnifiedInput
              control={methods.control}
              name="shipperInfo.companyName"
              label={t("shipperInfo.companyNameLabel")}
              wrapperClassName="w-full"
              disabled={shipperType === "PELICAN_AIR_SERVICES"}
            />
            <ControlledUnifiedInput
              control={methods.control}
              name="shipperInfo.contactName"
              label={t("shipperInfo.contactNameLabel")}
              required
              className="w-full"
              wrapperClassName="w-full"
              disabled={shipperType === "PELICAN_AIR_SERVICES"}
            />
          </div>
          <div className="flex flex-col gap-2 @xl:flex-row">
            <StyledPhoneInput
              control={methods.control}
              name="shipperInfo.phone"
              title={t("shipperInfo.phoneLabel")}
              required
              defaultCountry={(countryCode as CountryCode) || "FR"}
              disabled={shipperType === "PELICAN_AIR_SERVICES"}
            />
            <ControlledUnifiedInput
              label={t("shipperInfo.emailLabel")}
              required
              control={methods.control}
              name="shipperInfo.email"
              className="w-full"
              wrapperClassName="w-full"
              disabled={shipperType === "PELICAN_AIR_SERVICES"}
            />
          </div>
          <ControlledCountryComboboxCountryCode
            label={t("shipperInfo.countryLabel")}
            required
            control={methods.control}
            name="shipperInfo.countryCode"
            wrapperClassName="w-full"
            isDisabled={shipperType === "PELICAN_AIR_SERVICES"}
          />
          <div className="flex w-full flex-row items-start gap-2">
            <Controller
              name={`shipperInfo.streetLines.0`}
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <GooglePlaceAutocomplete
                  label={t("shipperInfo.streetLabel")}
                  required
                  value={field.value || ""}
                  onInputChange={(val) => field.onChange(val)}
                  onPlaceSelect={handlePlaceSelect}
                  error={translateMessage(error?.message)}
                  wrapperClassName="w-full min-w-0"
                  countryRestrictions={countryCode ? [countryCode] : undefined}
                  disabled={shipperType === "PELICAN_AIR_SERVICES"}
                />
              )}
            />
            <div className="flex h-14 shrink-0 flex-col">
              <FedexAddressValidationActions variant="shipper" />
            </div>
          </div>
          <ControlledUnifiedInput
            control={methods.control}
            name="shipperInfo.streetLines.1"
            label={t("shipperInfo.addressComplementLabel")}
            className="w-full"
            wrapperClassName="w-full"
            disabled={shipperType === "PELICAN_AIR_SERVICES"}
          />
          <div className="flex flex-col gap-2 @xl:flex-row">
            {showPostalCode && (
              <ControlledUnifiedInput
                className="w-full"
                control={methods.control}
                name="shipperInfo.postalCode"
                label={t("shipperInfo.postalCodeLabel")}
                required
                wrapperClassName="w-full"
                disabled={shipperType === "PELICAN_AIR_SERVICES"}
              />
            )}
            <ControlledUnifiedInput
              className="flex-1"
              control={methods.control}
              name="shipperInfo.city"
              label={t("shipperInfo.cityLabel")}
              required
              wrapperClassName="w-full"
              disabled={shipperType === "PELICAN_AIR_SERVICES"}
            />
            {showStateOrProvince && (
              <ControlledUnifiedInput
                className="w-full"
                control={methods.control}
                name="shipperInfo.stateOrProvinceCode"
                label={t("shipperInfo.stateOrProvinceLabel")}
                required
                wrapperClassName="w-full"
                disabled={shipperType === "PELICAN_AIR_SERVICES"}
              />
            )}
          </div>
          <ControlledUnifiedInput
            label={t("shipperInfo.taxIdLabel")}
            control={methods.control}
            name="shipperInfo.taxId"
            className="w-full"
            wrapperClassName="w-full @xl:w-1/2"
            disabled={shipperType === "PELICAN_AIR_SERVICES"}
          />
          <ControlledCheckbox
            label={t("shipperInfo.residentialLabel")}
            control={methods.control}
            name="shipperInfo.residential"
            disabled={shipperType === "PELICAN_AIR_SERVICES"}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
